<template>
  <div id="app" class="app-root">
    <!-- 顶部导航条：底色用后台配的主题色；标题居中，logo 单独钉在最左 -->
    <header class="navbar" :style="{ backgroundColor: themeColor }">
      <div class="nav-inner">
        <img v-if="site.logo" class="nav-logo" :src="site.logo" alt="">
        <h1 class="nav-title">{{ site.brand_name || '卫康生物' }}</h1>
      </div>
    </header>

    <main class="page">
      <!-- 骨架屏：接口很快时一闪而过，比白屏好看 -->
      <template v-if="loading">
        <section class="card">
          <div class="skeleton skeleton-banner"></div>
          <div class="skeleton-row" v-for="n in 4" :key="'sk' + n">
            <div class="skeleton skeleton-icon"></div>
            <div class="skeleton skeleton-text"></div>
          </div>
        </section>
      </template>

      <!-- 加载失败：给一个明确的重试入口，不要让用户对着白屏猜 -->
      <template v-else-if="error">
        <section class="card">
          <div class="empty">
            <p class="empty-text">{{ error }}</p>
            <button class="btn-retry" type="button" @click="load">重新加载</button>
          </div>
        </section>
      </template>

      <!-- 降级：一个启用中的目录都没有 → 整页显示「关于卫康」 -->
      <template v-else-if="!hasGroups">
        <section v-if="site.banner" class="banner-static">
          <img :src="site.banner" alt="">
        </section>
        <section class="card">
          <div class="intro-title">{{ site.intro_title }}</div>
          <p class="intro-desc">{{ site.intro_desc }}</p>
        </section>
        <section v-if="showWelfare" class="card">
          <div class="section-title">{{ site.welfare_title }}</div>
          <div v-for="(w, i) in welfareItems" :key="'w' + i" class="welfare-item">
            <div class="welfare-title">{{ w.title }}</div>
            <div v-if="w.desc" class="welfare-desc">{{ w.desc }}</div>
          </div>
        </section>
        <section v-if="showContact" class="card">
          <div class="section-title">{{ site.contact_title }}</div>
          <div class="contact-item" v-if="site.contact_address">
            <span class="contact-label">地址</span>
            <span class="contact-value">{{ site.contact_address }}</span>
          </div>
          <div class="contact-item" v-if="site.contact_phone">
            <span class="contact-label">服务热线</span>
            <span class="contact-value contact-strong">{{ site.contact_phone }}</span>
          </div>
          <div class="contact-item" v-if="site.contact_website" @click="goWebsite">
            <span class="contact-label">官网</span>
            <span class="contact-value contact-strong">{{ site.contact_website }}</span>
          </div>
        </section>
      </template>

      <!-- 正常：轮播 + 目录列表（B 方案：微信列表观感） -->
      <template v-else>
        <section v-if="banners.length" class="banner-wrap">
          <div class="banner-track" :style="bannerTrackStyle">
            <div
              v-for="(b, i) in banners"
              :key="'b' + i"
              class="banner-slide"
              @click="go(b)"
            >
              <img :src="b.image" :alt="b.title || ''">
              <div v-if="b.title" class="banner-caption">{{ b.title }}</div>
            </div>
          </div>
          <div v-if="banners.length > 1" class="banner-dots">
            <i
              v-for="(b, i) in banners"
              :key="'d' + i"
              class="dot"
              :class="{ on: i === bannerIndex }"
              @click="bannerIndex = i"
            ></i>
          </div>
        </section>

        <section v-for="g in groups" :key="g.id" class="card">
          <div class="group-head">
            <span class="group-name">{{ g.name }}</span>
            <span v-if="g.description" class="group-desc">{{ g.description }}</span>
          </div>
          <div
            v-for="it in g.items"
            :key="it.id"
            class="cell"
            :class="{ 'cell-static': it.link_type === 'none' }"
            @click="go(it)"
          >
            <img v-if="it.image" class="cell-icon-img" :src="it.image" alt="">
            <span v-else class="cell-icon" :style="iconStyle">{{ initial(it.title) }}</span>
            <span class="cell-main">
              <span class="cell-title">{{ it.title }}</span>
              <span v-if="it.subtitle" class="cell-sub">{{ it.subtitle }}</span>
            </span>
            <i v-if="it.link_type !== 'none'" class="cell-arrow"></i>
          </div>
        </section>
      </template>

      <footer class="footer">
        <span v-for="(line, i) in footerLines" :key="'f' + i">{{ line }}</span>
      </footer>
    </main>
  </div>
</template>

<script>
const DEFAULT_THEME = '#008F92'

/**
 * 后端接口地址
 *
 * 取值来自根目录的 .env.development / .env.production，
 * 改地址不必动代码，也不用重建宿主配置。
 *
 * ⚠️ 换地址必须同步改 wkadmin 的 .env：
 *      [request]
 *      cors_domain = localhost,<新域名>
 *    否则 FastAdmin 的 check_cors_request() 会直接 403「跨域检测无效」，
 *    浏览器控制台报的是 CORS 错误，比域名解析失败更难定位。
 *
 * 兜底值只在 .env 缺失时生效（比如刚 clone 下来还没配好），别指望它。
 */
const API_BASE = process.env.VUE_APP_API_BASE || 'http://act.cc'

export default {
  name: 'App',

  data() {
    return {
      loading: true,
      error: '',
      site: {},
      banners: [],
      groups: [],
      bannerIndex: 0,
      bannerTimer: null
    }
  },

  computed: {
    themeColor() {
      return this.site.theme_color || DEFAULT_THEME
    },

    // 一个启用中的目录都没有 → 降级为关于页（后端 nav 接口返回空 groups）
    hasGroups() {
      return this.groups.length > 0
    },

    welfareItems() {
      return Array.isArray(this.site.welfare_items) ? this.site.welfare_items : []
    },

    showWelfare() {
      return !!(this.site.welfare_title && this.welfareItems.length)
    },

    showContact() {
      if (!this.site.contact_title) return false
      return !!(this.site.contact_address || this.site.contact_phone || this.site.contact_website)
    },

    footerLines() {
      return Array.isArray(this.site.footer_lines) ? this.site.footer_lines : []
    },

    bannerTrackStyle() {
      return { transform: 'translateX(-' + (this.bannerIndex * 100) + '%)' }
    }
  },

  created() {
    this.load()
  },

  beforeDestroy() {
    this.stopAutoPlay()
  },

  methods: {
    load() {
      this.loading = true
      this.error = ''

      Promise.all([this.fetchJson('api/menu/nav'), this.fetchJson('api/menu/site')])
        .then(([navRes, siteRes]) => {
          // 两个接口谁失败都不能让整页白屏：
          // nav 挂了才报错；site 挂了只是少个标题而已，不值得拦人。
          if (!navRes || navRes.code !== 1) {
            throw new Error((navRes && navRes.msg) || '服务暂时不可用')
          }

          const data = navRes.data || {}
          this.banners = Array.isArray(data.banners) ? data.banners : []
          this.groups = Array.isArray(data.groups) ? data.groups : []

          if (siteRes && siteRes.code === 1 && siteRes.data) {
            this.site = siteRes.data
          }

          this.loading = false
          this.startAutoPlay()
        })
        .catch((e) => {
          this.loading = false
          this.groups = []
          this.error = e && e.message ? e.message : '加载失败，请稍后重试'
        })
    },

    fetchJson(path) {
      return fetch(API_BASE + '/' + path, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        // 不带 cookie：本接口免登录，带了反而会被跨域预检拦住
        credentials: 'omit'
      })
        .then((res) => res.json())
        .catch(() => null)
    },

    // 标题首字，作为没有上传图标时的兜底方块
    initial(title) {
      const text = String(title || '').trim()
      return text ? text.charAt(0).toUpperCase() : '·'
    },

    // 没有上传图标时，兜底方块用主题色，保持整页色调统一
    iconStyle() {
      return {
        backgroundColor: this.themeColor,
        color: '#fff'
      }
    },

    goWebsite() {
      const url = this.site.contact_website_url
      if (url) {
        window.location.href = url
      }
    },

    /**
     * 统一的点击分发
     *
     * 先打埋点再跳转 —— 但埋点是 fire-and-forget，失败绝不阻断跳转。
     * 埋点接口挂掉导致用户点不动，是比少几个统计数据严重得多的事故。
     */
    go(item) {
      if (!item || item.link_type === 'none') {
        return
      }

      this.track(item.id)

      if (item.link_type === 'url') {
        window.location.href = item.link_url
        return
      }

      if (item.link_type === 'mp') {
        this.goMiniProgram(item.link_url)
      }
    },

    /**
     * 跳小程序
     *
     * 后台 link_url 允许三种写法，这里按形态分派：
     *
     *   1) 小程序口令  #小程序://卫康服务/GDBXhbndSI3JXve  ← 当前实际在用的
     *      ⚠️ 必须用 DOM 上的 <a> + click() 触发。
     *         直接 location.href = '#小程序://…' 只会被浏览器当成页内锚点，
     *         页面纹丝不动，用户完全不知道发生了什么。
     *      同时把口令复制到剪贴板：万一微信没接管这次跳转，
     *      用户可以直接粘到聊天窗口里打开，不至于卡死在这一步。
     *
     *   2) URL Link   weixin://dl/business/?t=xxx
     *      协议跳转，交给 location.href。
     *
     *   3) 裸短码      GDBXhbndSI3JXve
     *      补上 URL Link 前缀。
     */
    goMiniProgram(linkUrl) {
      const raw = String(linkUrl || '').trim()

      if (!raw) {
        return
      }

      if (raw.charAt(0) === '#') {
        this.copyToClipboard(raw)

        const link = document.createElement('a')
        link.href = raw
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        return
      }

      if (raw.indexOf('weixin://') === 0) {
        window.location.href = raw
        return
      }

      window.location.href = 'weixin://dl/business/?t=' + encodeURIComponent(raw)
    },

    /**
     * 复制到剪贴板
     *
     * 只在微信里的小程序口令分支用到，失败就放弃 ——
     * 拿不到剪贴板权限不该影响主跳转流程。
     */
    copyToClipboard(text) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(text).catch(function () {})
          return
        }

        const input = document.createElement('textarea')
        input.value = text
        input.setAttribute('readonly', 'readonly')
        input.style.position = 'fixed'
        input.style.left = '-9999px'
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
      } catch (e) {
        // 静默
      }
    },

    track(id) {
      if (!id) return
      try {
        fetch(API_BASE + '/api/menu/click?id=' + encodeURIComponent(id), {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'omit'
        }).catch(function () {})
      } catch (e) {
        // 静默
      }
    },

    startAutoPlay() {
      this.stopAutoPlay()
      // 只有一张图时不轮播：省掉无意义的定时器，也避免用户以为页面卡住
      if (this.banners.length < 2) {
        return
      }
      this.bannerTimer = setInterval(() => {
        this.bannerIndex = (this.bannerIndex + 1) % this.banners.length
      }, 4000)
    },

    stopAutoPlay() {
      if (this.bannerTimer) {
        clearInterval(this.bannerTimer)
        this.bannerTimer = null
      }
    }
  }
}
</script>

<style>
/* 全局重置
   ------------------------------------------------------------------
   浏览器默认 body 有 8px 外边距。不清掉的话，页面四周会露出 body 的白色，
   看起来就是「四周有个白色框」。body 自身也要铺底色 —— 内容不满一屏时，
   露出的那一截同样是白的。 */
html,
body {
  margin: 0;
  padding: 0;
  height: 100%;
  background: #f5f5f5;
}

/* 微信风格视觉基线
   ------------------------------------------------------------------
   取值来源：jds-uniapp「关于卫康」页（pages/user/about.vue + components/nav-bar.vue）
   主色 #008F92（卫康生物）、页面底 #f5f5f5、白卡、分割线 #f5f5f5、文字 #333/#666/#999
   列表按压反馈 #ECECEC、右箭头灰 #C8C7CC —— 这几个是微信客户端的观感关键，
   换成自定义色就会立刻不像微信。 */
.app-root {
  --wk-bg: #f5f5f5;
  --wk-card: #ffffff;
  --wk-text: #333333;
  --wk-text-sub: #666666;
  --wk-text-weak: #999999;
  --wk-line: #f5f5f5;
  --wk-press: #ececec;
  --wk-arrow: #c8c7cc;
  --wk-radius: 8px;

  min-height: 100vh;
  background: var(--wk-bg);
  color: var(--wk-text);
  font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', 'PingFang SC',
    'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
  font-size: 15px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  -webkit-tap-highlight-color: transparent;
}

/* 顶部导航条 --------------------------------------------------------- */
.navbar {
  position: sticky;
  top: 0;
  z-index: 100;
  /* 安全区只在 navbar 上做一次，nav-inner 保持固定 44px 高，
     logo 的垂直居中才好算（top:50% 不会被 padding 顶下去） */
  padding-top: env(safe-area-inset-top);
}

.nav-inner {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  /* 左右等宽，标题才是真正的屏幕居中。
     不对称的话内容盒中心会偏（比如 16 / 100 时偏左 42px，肉看着就没居中）。
     60px 同时给右上角胶囊（宽约 87px）留了余量。 */
  padding: 0 60px;
  box-sizing: border-box;
}

/* logo 不参与居中：脱离文档流，钉在整条的最左边 */
.nav-logo {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  height: 22px;
}

.nav-title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: #ffffff;
  /* 品牌名过长时省略，而不是把 logo 挤出屏幕 */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 主体 --------------------------------------------------------------- */
.page {
  padding: 10px 10px 0;
  /* 底部安全区：没有它，iPhone 底部横条会盖住最后一行的版权信息 */
  padding-bottom: calc(20px + env(safe-area-inset-bottom));
}

.card {
  background: var(--wk-card);
  border-radius: var(--wk-radius);
  margin-bottom: 10px;
  overflow: hidden;
}

/* 骨架屏 ------------------------------------------------------------- */
.skeleton {
  background: linear-gradient(90deg, #eeeeee 25%, #f7f7f7 37%, #eeeeee 63%);
  background-size: 400% 100%;
  animation: sk-loading 1.2s ease infinite;
}

.skeleton-banner {
  height: 150px;
  border-radius: var(--wk-radius);
}

.skeleton-row {
  display: flex;
  align-items: center;
  padding: 14px 15px;
  border-top: 1px solid var(--wk-line);
}

.skeleton-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  flex: none;
}

.skeleton-text {
  height: 15px;
  margin-left: 12px;
  flex: 1;
}

@keyframes sk-loading {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

/* 错误 / 空状态 ------------------------------------------------------ */
.empty {
  padding: 40px 20px;
  text-align: center;
}

.empty-text {
  margin: 0 0 14px;
  color: var(--wk-text-weak);
  font-size: 14px;
}

.btn-retry {
  display: inline-block;
  padding: 7px 20px;
  font-size: 14px;
  color: var(--wk-text-sub);
  background: #f2f2f2;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.btn-retry:active {
  background: #e2e2e2;
}

/* 轮播 --------------------------------------------------------------- */
.banner-wrap {
  position: relative;
  margin-bottom: 10px;
  border-radius: var(--wk-radius);
  overflow: hidden;
  background: var(--wk-card);
}

.banner-static {
  margin-bottom: 10px;
  border-radius: var(--wk-radius);
  overflow: hidden;
  background: var(--wk-card);
}

.banner-static img {
  display: block;
  width: 100%;
}

.banner-track {
  display: flex;
  transition: transform 0.35s ease;
}

.banner-slide {
  position: relative;
  flex: none;
  width: 100%;
  cursor: pointer;
}

.banner-slide img {
  display: block;
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.banner-caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 20px 12px 8px;
  color: #ffffff;
  font-size: 13px;
  text-align: center;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0));
}

.banner-dots {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 8px;
  text-align: center;
}

.banner-dots .dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin: 0 3px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
}

.banner-dots .dot.on {
  background: #ffffff;
}

/* 目录分组 ----------------------------------------------------------- */
.group-head {
  display: flex;
  align-items: baseline;
  padding: 14px 15px 8px;
}

.group-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--wk-text);
  margin-right: 8px;
}

.group-desc {
  font-size: 12px;
  color: var(--wk-text-weak);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* 菜单项：微信列表观感 ------------------------------------------------ */
.cell {
  display: flex;
  align-items: center;
  padding: 13px 15px;
  border-top: 1px solid var(--wk-line);
  cursor: pointer;
}

/* 分割线不贴边：与文字左对齐，这是微信列表的细节 */
.group-head + .cell {
  border-top: none;
}

.cell:active {
  background: var(--wk-press);
}

.cell-static {
  cursor: default;
}

.cell-static:active {
  background: transparent;
}

.cell-icon,
.cell-icon-img {
  width: 28px;
  height: 28px;
  flex: none;
  margin-right: 12px;
  border-radius: 6px;
}

.cell-icon-img {
  object-fit: cover;
}

.cell-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 600;
  opacity: 0.92;
}

.cell-main {
  flex: 1;
  min-width: 0;
}

.cell-title {
  display: block;
  font-size: 15px;
  color: var(--wk-text);
}

.cell-sub {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: var(--wk-text-weak);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.cell-arrow {
  flex: none;
  width: 8px;
  height: 8px;
  margin-left: 8px;
  border-top: 1.5px solid var(--wk-arrow);
  border-right: 1.5px solid var(--wk-arrow);
  transform: rotate(45deg);
}

/* 关于页 ------------------------------------------------------------- */
.intro-title {
  font-size: 16px;
  font-weight: 600;
  padding: 16px 15px 0;
}

.intro-desc {
  margin: 8px 0 0;
  padding: 0 15px 16px;
  font-size: 13px;
  color: var(--wk-text-sub);
  line-height: 1.7;
}

.section-title {
  padding: 15px 15px 6px;
  font-size: 15px;
  font-weight: 600;
}

.welfare-item {
  padding: 11px 15px;
  border-top: 1px solid var(--wk-line);
}

.welfare-title {
  font-size: 14px;
  font-weight: 600;
}

.welfare-desc {
  margin-top: 3px;
  font-size: 12px;
  color: var(--wk-text-weak);
}

.contact-item {
  display: flex;
  align-items: flex-start;
  padding: 11px 15px;
  border-top: 1px solid var(--wk-line);
}

.contact-label {
  flex: none;
  width: 60px;
  font-size: 13px;
  color: var(--wk-text-weak);
}

.contact-value {
  flex: 1;
  font-size: 13px;
  color: var(--wk-text);
  word-break: break-all;
}

.contact-strong {
  color: #008f92;
  font-weight: 600;
}

/* 页脚 --------------------------------------------------------------- */
.footer {
  padding: 24px 16px 8px;
  text-align: center;
  font-size: 12px;
  color: var(--wk-text-weak);
}

.footer span {
  display: block;
  line-height: 1.8;
}
</style>

